import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    inject,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@environments/environment';
import {
    KeplerCoveoService,
    KeplerCoveoSuggestion,
    KeplerCoveoSuggestionsResponse,
    KeplerSfdcService,
    ProductService,
    SupportRecommendationsService,
    UserService,
} from '@services';
import {
    BehaviorSubject,
    Observable,
    Subscription,
    catchError,
    combineLatestWith,
    filter,
    firstValueFrom,
    iif,
    of,
    switchMap,
    tap,
    timer,
} from 'rxjs';
import { BreakpointService } from 'src/app/@shared/services/breakpoint/breakpoint.service';
import { GknRoutePath } from 'src/app/app-routing.enum';
import { RecentlyViewedService } from 'src/app/search-results/headless-search/services/recently-viewed.service';
import { SearchSuggestionsComponent } from '../../@shared/components/search-suggestions/search-suggestions.component';
import { AppHeaderSearchSuggestionsPipe } from './app-header-search-bar-suggestions.pipe';

@Component({
    selector: 'app-header-search-bar',
    imports: [
        CommonModule,
        AppHeaderSearchSuggestionsPipe,
        FormsModule,
        MatButtonModule,
        MatDividerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        ReactiveFormsModule,
        SearchSuggestionsComponent,
    ],
    templateUrl: './app-header-search-bar.component.html',
    styleUrls: ['./app-header-search-bar.component.scss'],
})
export class AppHeaderSearchBarComponent implements OnInit, OnChanges, OnDestroy {
    private breakpointService = inject(BreakpointService);
    private cdr = inject(ChangeDetectorRef);
    private el = inject(ElementRef);
    private keplerCoveoService = inject(KeplerCoveoService);
    private keplerSfdcService = inject(KeplerSfdcService);
    private productService = inject(ProductService);
    private router = inject(Router);
    private supportRecommendationsService = inject(SupportRecommendationsService);
    private recentlyViewedService = inject(RecentlyViewedService);
    private userService = inject(UserService);

    /** indicates whether this component is being shown in a mobile viewport */
    isMobile$: Observable<boolean>;
    /** indicates whether the search bar is visible */
    @Input() isOpen: boolean;
    /** void event that is fired after a search is submitted */
    @Output() afterSearch = new EventEmitter<void>();
    /** form group for user input */
    formGroup = new FormGroup({
        query: new FormControl('', Validators.minLength(1)),
    });
    /** element ref to the {@link searchTerm} form control */
    @ViewChild('searchTermRef') searchTermRef: ElementRef;
    /** css visibility state of the suffix div */
    suffixState: 'hidden' | 'visible' = 'hidden';
    /** search suggestions from Coveo */
    suggestions$: BehaviorSubject<KeplerCoveoSuggestion[]> = new BehaviorSubject([]);
    /** index of the selected suggestion via arrow up/down keys */
    private suggestionIndex$: BehaviorSubject<number> = new BehaviorSubject(-1);
    /** flag to show/hide search suggestions container */
    showSearchSuggestions = false;
    private subs: Subscription[] = [];

    constructor() {
        this.subs.push(
            this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
                this.showSearchSuggestions = false;
                this.clearSuggestions();
            }),
        );
    }

    /**
     * Initialize component and subscribe to router navigation events.
     * Clears suggestions when navigation occurs.
     */
    ngOnInit(): void {
        this.isMobile$ = this.breakpointService.mobile$;
    }

    /**
     * Handle input changes and update component state.
     * @param changes - SimpleChanges object containing changed properties
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isOpen']) {
            this.isOpen = changes['isOpen'].currentValue;
            if (!this.isOpen) this.clearSuggestions();
        }

        this.cdr.detectChanges();
    }

    /**
     * Focus the search input element
     */
    focus(): void {
        this.searchTermRef?.nativeElement?.focus();
    }

    /**
     * Blur the search input element
     */
    blur(): void {
        this.searchTermRef?.nativeElement?.blur();
    }

    /**
     * Emits the active search term text.
     * @param event click event from html template
     */
    handleSearch(event?: Event): void {
        event?.stopPropagation();

        let millis = 0;
        if (this.suggestionIndex$.value > -1) {
            this.selectSuggestion(this.suggestions$.value[this.suggestionIndex$.value]);
            millis = 250; // allow some time for form control to populate for better UX
        }

        // Clear suggestions immediately when search is performed
        this.clearSuggestions();
        this.showSearchSuggestions = false;
        this.blur();

        timer(millis) // timer is a one-time subscription and does not need unsubscribed
            .pipe(
                switchMap(() => this.userService.isAuthenticated$),
                switchMap((isAuthenticated) => {
                    return iif(() => isAuthenticated == true, this.keplerSfdcService.supportCases$, of([]));
                }),
                switchMap((cases) => {
                    const search = this.formGroup.controls.query.value;
                    console.debug('searching...', search);

                    // if the user searches a case number, open the case in a new tabdd
                    const found = cases?.find((item) => item.case == search);
                    if (found) {
                        window.open(`${environment.mySupport.url}?case=${found.case}`, '_blank');
                        return of(null);
                    } else {
                        return of(search);
                    }
                }),
                combineLatestWith(this.productService.selectedProduct$),
                tap(([search, product]) => {
                    if (search && this.isOpen) {
                        // be mindful of order here in this if statement

                        this.supportRecommendationsService.add(search); // no need to wait for this Promise to complete
                        this.recentlyViewedService.addSearch(search);

                        this.handleClear(); // we already have the search term in a variable here, so we can safely clear the user input

                        // emit "after search" [is stored into a variable] to parent components
                        // but before we actually route to the search page.
                        this.afterSearch.emit();

                        this.router.navigate([`/${GknRoutePath.SEARCH}`], {
                            queryParams: { q: search, product: product.uid },
                            queryParamsHandling: 'merge',
                        });
                    }
                }),
            )
            .subscribe();
    }

    /**
     * Emits the selected suggestion as a search term
     * @param suggestion to select
     */
    searchSuggestion(suggestion: KeplerCoveoSuggestion): void {
        this.selectSuggestion(suggestion);
        this.clearSuggestions(); // Clear suggestions before performing search
        this.handleSearch();
    }

    /**
     * Handles all keyup events
     * @param event key event
     */
    keyup(event: Event): void {
        event.stopPropagation();
        const value = this.formGroup.controls.query.value;

        // Show suggestions when input is empty, hide when typing
        this.showSearchSuggestions = !value || value.trim() === '';

        if (this.formGroup.controls.query.value == '') {
            this.clearSuggestions();
        } else {
            firstValueFrom(
                this.keplerCoveoService.suggestions$(this.formGroup.controls.query.value).pipe(
                    catchError((error) => {
                        console.error('Error fetching suggestions:', error);
                        return of({ data: [] }); // Return empty suggestions on error
                    }),
                ),
            )
                .then((suggestions: KeplerCoveoSuggestionsResponse) => {
                    const sorted =
                        suggestions.data?.sort((a, b) => a.executableConfidence - b.executableConfidence) || [];

                    this.suggestions$.next(sorted);
                })
                .catch((error) => {
                    console.debug('Failed to process suggestions:', error);
                    this.suggestions$.next([]); // Clear suggestions on error
                });
        }
    }

    /**
     * Handles key events for the Up arrow key
     * Increments the {@link suggestionIndex$}.
     * @param event key event
     */
    arrowUp(event: Event) {
        event.stopPropagation();
        if (this.suggestionIndex$.value == -1) {
            // set the index to the last suggestion
            this.suggestionIndex$.next(this.suggestions$.value.length - 1);
        } else if (this.suggestionIndex$.value > -1) {
            // decrement the index
            this.suggestionIndex$.next(this.suggestionIndex$.value - 1);
        }
    }

    /**
     * Handles key events for the Down arrow key
     * Decrements the {@link suggestionIndex$}.
     * @param event key event
     */
    arrowDown(event: Event) {
        event.stopPropagation();
        if (this.suggestionIndex$.value == this.suggestions$.value.length - 1) {
            // reset the index
            this.suggestionIndex$.next(-1);
        } else if (this.suggestionIndex$.value >= -1) {
            // increment the index
            this.suggestionIndex$.next(this.suggestionIndex$.value + 1);
        }
    }

    /**
     * Sets the given suggestion as the current search term.
     * Does not submit the search
     * @param suggestion to set
     */
    selectSuggestion(suggestion: KeplerCoveoSuggestion): void {
        this.formGroup.controls.query.setValue(suggestion.expression);
        this.clearSuggestions();
        this.searchTermRef.nativeElement.focus();
    }

    /**
     * Emits an empty event for clearing the active search term text.
     * @param e click event
     */
    handleClear(e?: Event): void {
        e?.stopPropagation();
        this.formGroup.controls.query.setValue('');
        this.clearSuggestions();
    }

    /**
     * Clears the current suggestions list and resets the suggestion index.
     */
    private clearSuggestions(): void {
        this.suggestionIndex$.next(-1);
        this.suggestions$.next([]);
    }

    /**
     * Handles clicks outside of this component
     * Clears search suggestions
     * @param event click event
     */
    @HostListener('document:click', ['$event'])
    clickout(event: Event) {
        event.stopPropagation();
        const target = event.target as HTMLElement;

        // If no target, assume click is outside and clear suggestions
        if (!target) {
            this.clearSuggestions();
            this.showSearchSuggestions = false;
            return;
        }

        const isInsideSearchBar = this.el.nativeElement.contains(target);
        const isInsideSuggestions = target.closest('.suggestion-container');

        if (!isInsideSearchBar && !isInsideSuggestions) {
            this.clearSuggestions();
            this.showSearchSuggestions = false;
        }
    }

    /**
     * Handle focus event to show suggestions
     */
    onFocus(): void {
        this.showSearchSuggestions = true;
    }

    /**
     * Close suggestions panel and emit afterSearch to close the search bar
     */
    closeSuggestions(): void {
        this.showSearchSuggestions = false;
        this.clearSuggestions();
        this.afterSearch.emit();
    }

    /**
     * Unsubscribe from all subscriptions on destroy
     * @returns void
     */
    ngOnDestroy(): void {
        this.subs?.forEach((sub) => sub.unsubscribe());
    }
}
